import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Phone, Building2, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import ContactFormModal from '../../components/WhatsAppChat/ContactFormModal';
import { api } from '../../services/api';
import { formatPhone } from '../../services/whatsapp/waFormatters';

interface Client {
  id: number;
  empresa_id: number;
  nome: string;
  telefone: string;
  email: string;
  empresa_nome: string;
  cargo: string;
  departamento: string;
  tipo: string;
  principal: boolean;
  avaliacao_desativada?: boolean;
}

const tipoBadge: Record<string, { label: string; classes: string }> = {
  PF: { label: 'PF', classes: 'bg-[#007AFF]/10 text-[#007AFF]' },
  PJ: { label: 'PJ', classes: 'bg-[#34C759]/10 text-[#34C759]' },
};

export default function ClientsPage() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddContact, setShowAddContact] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [deletingClient, setDeletingClient] = useState<Client | null>(null);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const perPage = 12;

  const handleDeleteContact = async () => {
    if (!deletingClient) return;
    try {
      await api.delete(
        `/api/v1/empresas/${deletingClient.empresa_id}/contatos/${deletingClient.id}/`,
      );
      toast.success('Contato excluído com sucesso');
      setDeletingClient(null);
      fetchContatos();
    } catch {
      toast.error('Erro ao excluir contato');
    }
  };

  const fetchContatos = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, size: perPage };
      if (search) params.search = search;
      const res = await api.get('/api/v1/empresas/contatos/todos/', { params });
      const data = res.data?.data;
      const items = data?.items || [];
      setClients(items);
      setTotalItems(data?.total || items.length);
    } catch {
      setClients([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    const timer = setTimeout(fetchContatos, search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [fetchContatos]);

  // Abertura direta do modal de edição via ?edit=<id>&phone=<telefone>
  // (usado pelo botão "Editar Contato" do painel do chat, que abre em nova aba).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const editId = Number(params.get('edit'));
    const phone = params.get('phone') || '';

    let active = true;
    if (editId) {
      (async () => {
        try {
          // Busca pelo telefone localiza o contato mesmo fora da 1ª página da lista.
          const res = await api.get('/api/v1/empresas/contatos/todos/', {
            params: { page: 1, size: 50, search: phone || undefined },
          });
          const items: Client[] = res.data?.data?.items || [];
          const found = items.find(c => c.id === editId);
          if (active && found) {
            setEditingClient(found);
          } else if (active) {
            toast.error('Contato não encontrado para edição');
          }
        } catch {
          if (active) toast.error('Erro ao carregar contato para edição');
        } finally {
          // Limpa os query params para não reabrir o modal em refresh/navegação.
          const url = new URL(window.location.href);
          url.searchParams.delete('edit');
          url.searchParams.delete('phone');
          window.history.replaceState({}, '', url.pathname + url.search);
        }
      })();
    }
    return () => {
      active = false;
    };
    // Executa apenas uma vez no mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalPages = Math.max(1, Math.ceil(totalItems / perPage));

  const getInitials = (name: string) =>
    name
      .split(' ')
      .map(n => n[0])
      .slice(0, 2)
      .join('');

  const getTipo = (c: Client) => (c.empresa_nome ? 'PJ' : 'PF');

  return (
    <div className="min-h-screen bg-[#f5f5f7] p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="animate-fade-in mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Contatos</h1>
          <p className="mt-1 text-sm text-gray-500">Gerencie sua base de contatos</p>
        </div>
        <button
          type="button"
          onClick={() => setShowAddContact(true)}
          className="flex items-center justify-center gap-2 rounded-xl bg-green-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-green-700"
        >
          <Plus size={16} />
          Novo Contato
        </button>
      </div>

      {/* Search */}
      <div className="animate-slide-up mb-5">
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nome, empresa ou telefone..."
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full rounded-xl bg-white py-2.5 pl-10 pr-4 text-sm shadow-wa-apple transition-all focus:outline-none focus:ring-2 focus:ring-wa-500/50"
          />
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-green-200 border-t-green-600" />
        </div>
      )}

      {/* Empty */}
      {!loading && clients.length === 0 && (
        <div className="py-16 text-center text-gray-400">
          <p className="text-sm">Nenhum contato encontrado</p>
        </div>
      )}

      {/* Desktop Table */}
      {!loading && clients.length > 0 && (
        <div className="animate-slide-up hidden overflow-hidden rounded-2xl bg-white shadow-wa-apple-md md:block">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200/60">
                  <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Nome
                  </th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Telefone
                  </th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Empresa
                  </th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Cargo
                  </th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Tipo
                  </th>
                  <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Depto.
                  </th>
                  <th className="px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {clients.map(client => {
                  const tipo = getTipo(client);
                  const badge = tipoBadge[tipo] || tipoBadge.PF;
                  return (
                    <tr
                      key={client.id}
                      className="cursor-pointer border-b border-gray-100/60 transition-colors hover:bg-gray-50/80"
                      onClick={() => setEditingClient(client)}
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-wa-400 to-wa-600 text-xs font-bold text-white shadow-wa-apple">
                            {getInitials(client.nome)}
                          </div>
                          <div>
                            <span className="font-medium text-gray-900">{client.nome}</span>
                            {client.principal && (
                              <span className="ml-2 rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-semibold text-green-700">
                                Principal
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <Phone size={13} className="text-gray-400" />
                          {client.telefone ? formatPhone(client.telefone) : '—'}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <Building2 size={13} className="text-gray-400" />
                          {client.empresa_nome || '—'}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-gray-600">{client.cargo || '—'}</td>
                      <td className="px-5 py-3.5">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${badge.classes}`}
                        >
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-gray-600">{client.departamento || '—'}</td>
                      <td className="px-5 py-3.5 text-right">
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            setDeletingClient(client);
                          }}
                          className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
                          title="Excluir contato"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between border-t border-gray-100/60 px-5 py-3.5">
            <p className="text-sm text-gray-500">
              Mostrando{' '}
              <span className="font-semibold">
                {Math.min((page - 1) * perPage + 1, totalItems)}-
                {Math.min(page * perPage, totalItems)}
              </span>{' '}
              de <span className="font-semibold">{totalItems}</span> contatos
            </p>
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-white/80 disabled:opacity-40"
                disabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                <ChevronLeft size={18} />
              </button>
              <span className="px-3 py-1.5 text-sm text-gray-600">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                className="rounded-xl p-2 text-gray-600 transition-colors hover:bg-white/80 disabled:opacity-40"
                disabled={page >= totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Card View */}
      {!loading && clients.length > 0 && (
        <div className="animate-slide-up space-y-3 md:hidden">
          {clients.map(client => {
            const tipo = getTipo(client);
            const badge = tipoBadge[tipo] || tipoBadge.PF;
            return (
              <div
                key={client.id}
                role="button"
                tabIndex={0}
                className="hover-lift cursor-pointer rounded-2xl bg-white p-4 shadow-wa-apple transition-all"
                onClick={() => setEditingClient(client)}
                onKeyDown={e => e.key === 'Enter' && setEditingClient(client)}
              >
                <div className="mb-3 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-wa-400 to-wa-600 text-xs font-bold text-white shadow-wa-apple">
                    {getInitials(client.nome)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-gray-900">{client.nome}</p>
                    <p className="text-xs text-gray-500">{client.empresa_nome || '—'}</p>
                  </div>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${badge.classes}`}
                  >
                    {badge.label}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Phone size={11} />
                    {client.telefone ? formatPhone(client.telefone) : '—'}
                  </div>
                  <span>{client.departamento || '—'}</span>
                </div>
              </div>
            );
          })}

          {/* Mobile Pagination */}
          <div className="flex items-center justify-center gap-2 py-4">
            <button
              type="button"
              className="rounded-xl bg-white p-2 text-gray-400 shadow-wa-apple disabled:opacity-40"
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              <ChevronLeft size={18} />
            </button>
            <span className="px-3 text-sm text-gray-500">
              Pagina {page} de {totalPages}
            </span>
            <button
              type="button"
              className="rounded-xl bg-white p-2 text-gray-600 shadow-wa-apple disabled:opacity-40"
              disabled={page >= totalPages}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Modal de novo contato */}
      <ContactFormModal
        isOpen={showAddContact}
        onClose={() => setShowAddContact(false)}
        onSave={() => {
          setShowAddContact(false);
          fetchContatos();
        }}
      />

      {/* Modal de edição de contato */}
      {editingClient && (
        <ContactFormModal
          isOpen={!!editingClient}
          onClose={() => setEditingClient(null)}
          editContactId={editingClient.id}
          editData={{
            nome: editingClient.nome,
            telefone: editingClient.telefone,
            email: editingClient.email,
            cargo: editingClient.cargo,
            departamento: editingClient.departamento,
            empresaId: editingClient.empresa_id,
            principal: editingClient.principal,
            avaliacaoDesativada: editingClient.avaliacao_desativada,
          }}
          onSave={() => {
            setEditingClient(null);
            fetchContatos();
          }}
        />
      )}

      {/* Popup de confirmação de exclusão */}
      {deletingClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="mx-4 w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Excluir contato</h3>
            <p className="mt-2 text-sm text-gray-600">
              Tem certeza que deseja excluir <strong>{deletingClient.nome}</strong>?
            </p>
            <p className="mt-1 text-xs text-gray-400">
              As conversas não serão apagadas. O contato poderá ser cadastrado novamente.
            </p>
            <div className="mt-5 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeletingClient(null)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDeleteContact}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
